import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { Role } from '../../models/role.model';
import { authToken } from '../../scripts/authentication.script';

export class ApiDigitalAuthRole {
  static apiResponse: ApiResponse

  public static async getRoles(
    api_key: string,
    activeOnly: boolean = true
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_AUTH}/api/v1/${api_key}/company/roles${activeOnly ? '?active=true' : ''}`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.get(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data?.roles ?? response.data?.data ?? response.data?.roles ?? []
      }
    }).catch(error => {
      const errorCode = error.response?.data?.error?.code ?? error.response?.data?.code ?? ''
      if (error.response?.status === 403 && errorCode === 'COMPANY_ADMIN_REQUIRED') {
        this.apiResponse = { status: 200, message: 'No company context', data: [] as Role[] }
      } else {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      }
    })

    return this.apiResponse
  }

  public static async getInvitableRoles(
    api_key: string
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_AUTH}/api/v1/${api_key}/company/invitable-roles`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.get(url, config).then(response => {
      const rawRoles = response.data?.data?.roles ?? response.data?.data ?? response.data?.roles ?? []
      const normalized = Array.isArray(rawRoles)
        ? rawRoles.map((r: any) => ({ ...r, id: r.id ?? r._id }))
        : []
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: normalized
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async getRole(
    api_key: string,
    role_id: string
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_AUTH}/api/v1/${api_key}/company/roles/${role_id}`
    let config: any = { headers: { Authorization: authToken() } }

    await axios.get(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async createRole(
    api_key: string,
    payload: { name: string; description?: string; policies?: string[] }
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_AUTH}/api/v1/${api_key}/company/roles`
    let config: any = { headers: { Authorization: authToken() } }

    await axios.post(url, payload, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async updateRole(
    api_key: string,
    role_id: string,
    payload: { name?: string; description?: string; is_active?: boolean }
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_AUTH}/api/v1/${api_key}/company/roles/${role_id}`
    let config: any = { headers: { Authorization: authToken() } }

    await axios.put(url, payload, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async deleteRole(
    api_key: string,
    role_id: string
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_AUTH}/api/v1/${api_key}/company/roles/${role_id}`
    let config: any = { headers: { Authorization: authToken() } }

    await axios.delete(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async attachPolicy(
    api_key: string,
    role_id: string,
    policy_id: string
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_AUTH}/api/v1/${api_key}/company/roles/${role_id}/policies`
    let config: any = { headers: { Authorization: authToken() } }

    await axios.post(url, { policy_id }, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async detachPolicy(
    api_key: string,
    role_id: string,
    policy_id: string
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_AUTH}/api/v1/${api_key}/company/roles/${role_id}/policies/${policy_id}`
    let config: any = { headers: { Authorization: authToken() } }

    await axios.delete(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }
}
